import { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import api from '../api/client';
import { uploadNumbers, uploadSettingsPayload, UPLOAD_SETTINGS } from '../utils/orderUnits';

// qty и price проверяет uploadNumbers (принимает и старые имена колонок lbqty / price_per_pcs)
const REQUIRED_COLUMNS = ['seedsid', 'year', 'month_num', 'supplierName', 'statusID'];
const VALID_STATUSES   = ['S', 'OP', 'BO', 'D', 'C'];

/**
 * useUploadSeedsOrders
 * Hook for uploading seeds orders from a CSV/XLSX file.
 * Количество и цена в файле — в единице и валюте, выбранных в окне загрузки (settings);
 * канон lb / USD считает сервер.
 *
 * @param {Array}    suppliers  — seeds suppliers [{ supplierID, supplierName }]
 * @param {Function} onSuccess  — callback after successful save
 */
export const useUploadSeedsOrders = ({ suppliers = [], onSuccess }) => {
  const [settings,      setSettings]      = useState(UPLOAD_SETTINGS);
  const [parsedRows,    setParsedRows]    = useState([]);
  const [parseErrors,   setParseErrors]   = useState([]);
  const [orderID,       setOrderID]       = useState('');
  const [fileName,      setFileName]      = useState('');
  const [rawFile,       setRawFile]       = useState(null);
  const [isSubmitting,  setIsSubmitting]  = useState(false);
  const [submitResult,  setSubmitResult]  = useState(null);
  const [duplicateWarning, setDuplicateWarning] = useState(null); // { message, duplicates }

  const reset = useCallback(() => {
    setSettings(UPLOAD_SETTINGS);
    setParsedRows([]);
    setParseErrors([]);
    setOrderID('');
    setFileName('');
    setIsSubmitting(false);
    setSubmitResult(null);
    setDuplicateWarning(null);
  }, []);

  const parseFile = useCallback((file) => {
    setFileName(file.name);
    setRawFile(file);
    setParsedRows([]);
    setParseErrors([]);
    setSubmitResult(null);

    const supplierMap = {};
    suppliers.forEach(s => { supplierMap[s.supplierName.trim()] = s.supplierID; });

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        let rawRows = [];

        if (file.name.toLowerCase().endsWith('.csv')) {
          const text    = e.target.result;
          const lines   = text.split('\n').filter(l => l.trim());
          const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
          rawRows = lines.slice(1).map(line => {
            const values = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
            const obj = {};
            headers.forEach((h, i) => { obj[h] = values[i] ?? ''; });
            return obj;
          }).filter(r => Object.values(r).some(v => v !== ''));
        } else {
          const workbook = XLSX.read(e.target.result, { type: 'binary' });
          const sheet    = workbook.Sheets[workbook.SheetNames[0]];
          rawRows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
        }

        // ── Basic validation ─────────────────────────────────────────────────
        const allErrors = [];
        const numbers = rawRows.map(row => uploadNumbers(row, true));
        rawRows.forEach((row, index) => {
          const n = index + 1;
          for (const col of REQUIRED_COLUMNS) {
            if (row[col] === undefined || row[col] === null || String(row[col]).trim() === '') {
              allErrors.push(`Row ${n}: Missing required field "${col}"`);
            }
          }
          const supplierKey = row.supplierName?.toString().trim();
          if (supplierKey && !supplierMap[supplierKey]) {
            allErrors.push(`Row ${n}: Unknown supplier "${supplierKey}"`);
          }
          numbers[index].errors.forEach(err => allErrors.push(`Row ${n}: ${err}`));
          const status = row.statusID?.toString().trim().toUpperCase();
          if (status && !VALID_STATUSES.includes(status)) {
            allErrors.push(`Row ${n}: Invalid statusID "${status}". Valid: ${VALID_STATUSES.join(', ')}`);
          }
          if (status !== 'BO' && !row.est_del_date) {
            allErrors.push(`Row ${n}: "est_del_date" required when statusID is not BO`);
          }
          if (status === 'BO' && !row.bo_follow_up_date) {
            allErrors.push(`Row ${n}: "bo_follow_up_date" required when statusID is BO`);
          }
        });

        if (allErrors.length > 0) {
          setParseErrors(allErrors);
          return;
        }

        // ── Validate seedsid values are numbers ──────────────────────────────
        const idErrors = [];
        rawRows.forEach((row, index) => {
          const id = row.seedsid?.toString().trim();
          if (id && isNaN(parseInt(id))) {
            idErrors.push(`Row ${index + 1}: "seedsid" must be a number`);
          }
        });
        if (idErrors.length > 0) {
          setParseErrors(idErrors);
          return;
        }

        // ── Resolve seedsIDs → names for preview ─────────────────────────────
        const uniqueIDs = [...new Set(rawRows.map(r => parseInt(r.seedsid?.toString().trim())).filter(Boolean))];
        let resolvedMap = {};
        try {
          const res = await api.post('/api/material-orders/resolve-seeds-ids', { ids: uniqueIDs });
          res.data.forEach(r => { resolvedMap[r.seedsID] = r.name; });
        } catch {
          setParseErrors(['Failed to resolve seed IDs — server error']);
          return;
        }

        const resolveErrors = [];
        rawRows.forEach((row, index) => {
          const id = parseInt(row.seedsid?.toString().trim());
          if (id && !resolvedMap[id]) {
            resolveErrors.push(`Row ${index + 1}: Seed ID not found in database: "${id}"`);
          }
        });
        if (resolveErrors.length > 0) {
          setParseErrors(resolveErrors);
          return;
        }

        // ── Build clean rows ─────────────────────────────────────────────────
        const validRows = rawRows.map((row, index) => {
          const seedsID = parseInt(row.seedsid?.toString().trim());
          const status  = row.statusID.toString().trim().toUpperCase();
          const { qty, price, duties, lab_testing } = numbers[index];
          return {
            seedsID,
            seedsName:         resolvedMap[seedsID] || '',
            year:              parseInt(row.year),
            month_num:         parseInt(row.month_num),
            supplierName:      row.supplierName?.toString().trim(),
            supplierID:        supplierMap[row.supplierName?.toString().trim()],
            statusID:          status,
            qty,               // в единице окна загрузки (lb | kg)
            price,             // за эту единицу, в валюте окна загрузки
            duties,
            lab_testing,
            est_del_date:      status !== 'BO' ? (row.est_del_date || '') : null,
            bo_follow_up_date: status === 'BO' ? (row.bo_follow_up_date || '') : null,
            tracking:          row.tracking?.toString().trim() || '',
          };
        });

        setParsedRows(validRows);
        // Файл с пошлинами или анализами — импортный заказ: галочка ставится сама, снять можно
        if (validRows.some(r => r.duties > 0 || r.lab_testing > 0)) setSettings(prev => ({ ...prev, is_import: true }));
      } catch (err) {
        setParseErrors([`Failed to parse file: ${err.message}`]);
      }
    };

    if (file.name.toLowerCase().endsWith('.csv')) {
      reader.readAsText(file);
    } else {
      reader.readAsBinaryString(file);
    }
  }, [suppliers]);

  const submitOrders = useCallback(async (force = false) => {
    if (!orderID.trim()) {
      alert('Order ID is required');
      return;
    }
    if (parsedRows.length === 0) {
      alert('No valid rows to submit');
      return;
    }
    const payload = uploadSettingsPayload(settings, true);
    if (payload.error) {
      setSubmitResult({ success: false, message: payload.error });
      return;
    }

    setIsSubmitting(true);
    setSubmitResult(null);
    setDuplicateWarning(null);

    try {
      // Количество и цена уходят как введённые (qty_entered / price_entered), канон считает сервер
      const orders = parsedRows.map(({ qty, price, ...row }) => ({
        ...row, qty_entered: qty, price_entered: price, orderID: orderID.trim(),
      }));
      const response = await api.post('/api/material-orders/bulk-seeds', { orders, force, settings: payload.settings });

      // Save original file to server for audit archive
      if (rawFile) {
        try {
          const fd = new FormData();
          fd.append('file', rawFile);
          await api.post('/api/material-orders/save-file/seeds', fd);
        } catch { /* non-critical — don't block success */ }
      }

      setSubmitResult({ success: true, message: `Successfully saved ${response.data.count} seeds orders.` });
      if (onSuccess) onSuccess();
    } catch (error) {
      if (error.response?.status === 409) {
        setDuplicateWarning({
          message: error.response.data.message,
          duplicates: error.response.data.duplicates || [],
        });
      } else {
        setSubmitResult({ success: false, message: error.response?.data?.error || 'Failed to save orders' });
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [orderID, parsedRows, settings, rawFile, onSuccess]);

  return {
    settings, setSettings,
    parsedRows,
    parseErrors,
    orderID, setOrderID,
    fileName,
    isSubmitting,
    submitResult,
    duplicateWarning,
    dismissDuplicateWarning: () => setDuplicateWarning(null),
    parseFile,
    submitOrders,
    reset,
  };
};
